import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { LOCATION_TYPE_OPTIONS, LocationType } from '../../../shared/lib/locations';
import { Location, mensagemDeErro } from '../api';
import {
  useClientesParaLocalidade,
  useEmpresasParaLocalidade,
  useSalvarLocalidade,
} from '../hooks';
import { CadastroModal } from './CadastroModal';

const NENHUM = '';

interface Props {
  open: boolean;
  localidade: Location | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

/**
 * Cadastro de localidades (issue #300).
 *
 * RN03: nome, tipo e situação. O código é opcional — sem ele o backend gera
 * a partir do nome. RN05: só o tipo Cliente permite vincular cliente.
 * RN06: unidade da empresa pode apontar para a empresa proprietária.
 */
export function LocalidadeFormModal({ open, localidade, onClose, onSaved }: Props) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState<LocationType>('company_unit');
  const [clientId, setClientId] = useState(NENHUM);
  const [companyId, setCompanyId] = useState(NENHUM);

  const salvar = useSalvarLocalidade();
  const isCliente = type === 'client';
  const isUnidade = type === 'company_unit';
  const clientes = useClientesParaLocalidade(open && isCliente);
  const empresas = useEmpresasParaLocalidade(open && isUnidade);

  useEffect(() => {
    if (!open) return;
    setName(localidade?.name ?? '');
    setCode(localidade?.code ?? '');
    setType(localidade?.type ?? 'company_unit');
    setClientId(localidade?.clientId ?? NENHUM);
    setCompanyId(localidade?.companyId ?? NENHUM);
    salvar.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, localidade]);

  const clienteOptions = useMemo(
    () => [
      { value: NENHUM, label: 'Não vincular a um cliente' },
      ...(clientes.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [clientes.data],
  );

  const empresaOptions = useMemo(
    () => [
      { value: NENHUM, label: 'Nenhuma empresa específica' },
      ...(empresas.data ?? []).map((e) => ({ value: e.id, label: e.name })),
    ],
    [empresas.data],
  );

  function changeType(next: string) {
    const value = next as LocationType;
    setType(value);
    // O vínculo com cliente só vale para o tipo Cliente (RN05). Limpar ao
    // trocar evita erro de validação no backend.
    if (value !== 'client') setClientId(NENHUM);
    if (value !== 'company_unit') setCompanyId(NENHUM);
  }

  async function handleSubmit() {
    try {
      await salvar.mutateAsync({
        id: localidade?.id,
        payload: {
          name: name.trim(),
          code: code.trim() || undefined,
          type,
          clientId: isCliente ? clientId || null : null,
          companyId: isUnidade ? companyId || null : null,
        },
      });
      onSaved(localidade ? 'Localização atualizada.' : 'Localização cadastrada.');
    } catch {
      // Erro exibido no próprio modal.
    }
  }

  return (
    <CadastroModal
      open={open}
      title={localidade ? 'Editar localização' : 'Nova localização'}
      description="Onde o ativo está alocado — unidade, cliente, estoque, assistência técnica. Diferente do responsável pela guarda."
      submitLabel="Salvar"
      isSubmitting={salvar.isPending}
      submitError={
        salvar.isError
          ? mensagemDeErro(salvar.error, 'Não foi possível salvar a localização.')
          : null
      }
      canSubmit={name.trim() !== ''}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <TextInput
        label="Nome"
        value={name}
        maxLength={160}
        onChange={(e) => setName(e.target.value)}
      />
      <TextInput
        label="Código (opcional)"
        value={code}
        maxLength={40}
        onChange={(e) => setCode(e.target.value)}
      />
      <p className="-mt-2 text-xs text-gray-500">
        Sem código, o sistema gera um a partir do nome.
      </p>
      <AutocompleteSelect
        label="Tipo de localização"
        options={LOCATION_TYPE_OPTIONS}
        value={type}
        onChange={changeType}
      />
      {isCliente && (
        <AutocompleteSelect
          label="Cliente vinculado"
          options={clienteOptions}
          value={clientId}
          onChange={setClientId}
        />
      )}
      {isUnidade && (
        <AutocompleteSelect
          label="Empresa / unidade"
          options={empresaOptions}
          value={companyId}
          onChange={setCompanyId}
        />
      )}
      <p className="text-xs text-gray-500">
        Só localizações ativas aparecem para novas alocações. Inativar não
        apaga o histórico de movimentações do ativo.
      </p>
    </CadastroModal>
  );
}
